import { Button } from "components/Button/Button";
import { InputComponentProps } from "components/Form/components/InputComponentProps";
import { IconCollection } from "components/Icon/IconCollection";
import Tooltip from "components/Tooltip/Tooltip";
import { useEffect, useRef, useState } from "react";
import styles from "./CopyToClipboard.module.scss";

export interface CopyToClipboardProps {
    identifier: string;
    input: InputComponentProps<string>;
    attributes?: any; // TODO consider improvement
    textToCopy: string;
    isReadonly?: boolean;
}

export const CopyToClipboard = ({
    identifier,
    input,
    attributes,
    textToCopy,
    isReadonly,
}: CopyToClipboardProps) => {
    // Transient click-feedback: swap the copy icon for a check mark (and the
    // tooltip label for "Copied!") for ~1.2s after a successful copy, so the user
    // gets a Cloudomation-standard confirmation the click worked. Same pattern as
    // the ExpandableNotification copy button (icon swap to IconCollection.check).
    const [isCopied, setIsCopied] = useState(false);
    const copyResetTimer = useRef<ReturnType<typeof setTimeout>>();

    // Clear the pending reset on unmount — the component lives inside a modal that
    // can close before the timer fires, which would otherwise set state on an
    // unmounted component.
    useEffect(
        () => () => {
            if (copyResetTimer.current) {
                clearTimeout(copyResetTimer.current);
            }
        },
        []
    );

    const handleClick = () => {
        navigator.clipboard.writeText(textToCopy);
        setIsCopied(true);
        if (copyResetTimer.current) {
            clearTimeout(copyResetTimer.current);
        }
        copyResetTimer.current = setTimeout(() => setIsCopied(false), 1200);
    };
    const handleFocus = (event) => event.target.select();

    return (
        <div className={styles.copyToClipboardWrapper}>
            <input
                {...attributes}
                {...input}
                id={`${identifier}`}
                aria-label={`${identifier}`}
                type="text"
                className="input__field"
                onFocus={handleFocus}
                readOnly={isReadonly}
            />
            <Tooltip overlay={isCopied ? "Copied!" : "Copy"}>
                <Button
                    size="small"
                    icon={isCopied ? IconCollection.check : IconCollection.contentCopy}
                    onClick={handleClick}
                    variation="clear"
                />
            </Tooltip>
        </div>
    );
};
